import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { CheckCircle2, Smartphone, Tablet, Tv, ShieldCheck, Zap, Rocket, Star, Info, Download, LayoutGrid, ListChecks } from "lucide-react";
import { motion } from "framer-motion";

export const Route = createFileRoute("/tutoriais-iptv/android")({
  head: () => ({
    meta: [
      { title: "Como Instalar IPTV no Android: Guia Completo 2026" },
      { name: "description", content: "Aprenda como configurar IPTV no seu celular, tablet ou Android TV. Guia completo com apps oficiais da Play Store para Painel IPTV Pós Pago." },
    ],
  }),
  component: AndroidTutorial,
});

function AndroidTutorial() {
  const apps = [
    { name: "OneX Player", type: "Próprio", desc: "Nossa principal recomendação. Desenvolvido para máxima performance no ecossistema Android." },
    { name: "P2PRO", type: "Próprio", desc: "Aplicativo robusto com foco em estabilidade e facilidade de navegação." },
    { name: "P2PLAY", type: "Próprio", desc: "Leve e versátil, funcionando perfeitamente de celulares a Android TVs." },
    { name: "Prime Play", type: "Próprio", desc: "Interface rica e completa para quem busca a melhor experiência visual." },
    { name: "RP725", type: "Parceiro", desc: "Excelente alternativa estável com interface moderna e intuitiva." },
    { name: "VUser", type: "Parceiro", desc: "Player moderno com configuração simplificada e ótimo desempenho." }
  ];

  const devices = [
    { name: "Smartphones", icon: <Smartphone className="w-5 h-5" /> },
    { name: "Tablets", icon: <Tablet className="w-5 h-5" /> },
    { name: "Android TV", icon: <Tv className="w-5 h-5" /> },
    { name: "TV Box", icon: <LayoutGrid className="w-5 h-5" /> },
    { name: "Google TV", icon: <Tv className="w-5 h-5" /> }
  ];

  return (
    <SEOPage
      title="Como Instalar IPTV no Android: Guia Completo 2026"
      subtitle="Leve seu entretenimento para qualquer lugar com a plataforma mais versátil do mundo."
      category="Tutoriais Android"
      introParagraphs={[
        "Os dispositivos Android são os mais utilizados por usuários de IPTV devido à praticidade, compatibilidade com aplicativos modernos e facilidade de instalação. Seja em celulares, tablets, Android TV ou TV Box, é possível instalar aplicativos diretamente pela Google Play Store em poucos minutos.",
        "Trabalhamos com uma estrutura de Painel IPTV Pós Pago otimizada especificamente para o ecossistema Android, proporcionando a revendedores e clientes finais uma estabilidade de sinal incomparável e facilidade de ativação."
      ]}
      breadcrumbs={[
        { label: "Início", href: "/" },
        { label: "Tutoriais", href: "/tutoriais-iptv" },
        { label: "Android", href: "/tutoriais-iptv/android" }
      ]}
      benefits={[
        { title: "Play Store Oficial", description: "Segurança total baixando aplicativos diretamente da loja do Google." },
        { title: "Mobilidade Total", description: "Assista no celular, tablet ou na TV com a mesma conta." },
        { title: "Setup em 60 Segundos", description: "Instalação e login extremamente rápidos e simplificados." },
        { title: "Performance Fluida", description: "Otimizado para rodar em diversos níveis de hardware." },
        { title: "Gestão Pós-Paga", description: "O modelo mais lucrativo para revenda mobile e TV." },
        { title: "Suporte Técnico", description: "Equipe pronta para auxiliar em qualquer dispositivo Android." }
      ]}
      howItWorks={[
        { title: "Acesse a Loja", description: "Abra a Google Play Store no seu dispositivo Android." },
        { title: "Busca e Download", description: "Pesquise por 'OneX Player' ou outro app homologado." },
        { title: "Instalação", description: "Clique em instalar e aguarde o download automático." },
        { title: "Login e Acesso", description: "Insira seus dados do Painel Pós Pago e aproveite." }
      ]}
      diferenciais={[
        { title: "Sem Créditos Travados", description: "Cresça sem precisar investir em estoque de créditos antecipados." },
        { title: "Apps Próprios", description: "Desenvolvemos tecnologia própria para garantir zero quedas." },
        { title: "Ativação Rápida", description: "Libere o acesso do seu cliente em segundos pelo painel." },
        { title: "Marca Profissional", description: "Apresente uma estrutura de ponta para seus clientes." }
      ]}
      faq={[
        { question: "Posso instalar IPTV em qualquer celular Android?", answer: "Sim. A grande maioria dos smartphones Android atuais suporta nossos aplicativos oficiais da Play Store." },
        { question: "Preciso baixar arquivos APK?", answer: "Não é necessário. Nossos principais aplicativos estão disponíveis na Play Store, garantindo mais segurança." },
        { question: "O OneX Player funciona em tablet?", answer: "Sim, ele é otimizado tanto para telas pequenas de smartphones quanto para telas maiores de tablets e TVs." },
        { question: "Como funciona o Painel Pós Pago no Android?", answer: "Você gerencia tudo pelo celular ou computador e paga apenas pelo consumo real da sua rede." },
        { question: "Qual o melhor app para Android TV?", answer: "Recomendamos o OneX Player por ser desenhado especificamente para a experiência de TV." }
      ]}
      sections={[
        {
          title: "Compatibilidade Android",
          content: (
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {devices.map((device, i) => (
                <div key={i} className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-xs font-bold uppercase tracking-widest">
                  {device.icon}
                  {device.name}
                </div>
              ))}
            </div>
          )
        },
        {
          title: "Aplicativos Recomendados para Android",
          type: "grid",
          content: (
            <>
              {apps.map((app, i) => (
                <div key={i} className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-electric/30 transition-all group">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-white group-hover:text-electric transition-colors">{app.name}</h3>
                    <span className="text-[10px] font-black uppercase tracking-widest bg-electric/10 text-electric px-2 py-1 rounded-md">{app.type}</span>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed">{app.desc}</p>
                </div>
              ))}
            </>
          )
        },
        {
          title: "Por que escolher o Painel Pós Pago P2PRO?",
          type: "featured",
          content: (
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="bg-electric/20 p-3 rounded-2xl text-electric shrink-0">
                    <Zap className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Tecnologia de Ponta</h4>
                    <p className="text-muted-foreground text-sm">Servidores de alta performance com redundância para garantir sinal 24h.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="bg-electric/20 p-3 rounded-2xl text-electric shrink-0">
                    <ShieldCheck className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Segurança Financeira</h4>
                    <p className="text-muted-foreground text-sm">Ideal para quem busca escala sem riscos de capital parado em créditos.</p>
                  </div>
                </div>
              </div>
              <div className="bg-white/5 p-8 rounded-[2rem] border border-white/10">
                <Info className="w-8 h-8 text-electric mb-4" />
                <p className="text-sm text-muted-foreground italic leading-relaxed">
                  "O Android é a porta de entrada para a maioria dos clientes IPTV. Ter aplicativos oficiais na Play Store aumenta drasticamente a confiança do cliente e facilita sua venda."
                </p>
              </div>
            </div>
          )
        }
      ]}
      ctaTitle="🚀 Comece Sua Revenda Android Agora"
      ctaDescription="Tenha acesso aos melhores apps oficiais da Play Store e à maior infraestrutura Pós-Paga do Brasil."
    />
  );
}
